/**
 * @fileoverview Componente para iniciar órdenes de producción
 * Permite activar órdenes desde la interfaz de edición
 */

import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { UncontrolledTooltip } from 'reactstrap';
import { RepositoryFactory } from '../repositories';
import { createModuleLogger } from '../utils/logger';
import InventarioAllocationService from '../services/InventarioAllocationService';


const IniciarOrdenButton = ({ orden, onReload, onShowAlert, logger }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  
  // Crear logger para este componente
  const componentLogger = createModuleLogger('IniciarOrdenButton');
  
  // Obtener usuario del localStorage
  const getCurrentUser = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData.record;
      }
      return null;
    } catch (error) {
      componentLogger.error('Error al obtener usuario del localStorage', error);
      return null;
    }
  };

  // Verificar si la orden ya está activa
  const isOrdenActiva = !!orden.activa;

  // Función para abrir el modal de confirmación
  const handleOpenModal = async () => {
    if (!isOrdenActiva) {
      // Verificar materiales ANTES de abrir el modal
      try {
        componentLogger.info('Verificando materiales antes de abrir modal', { 
          ordenId: orden.id, 
          numero: orden.numero 
        });
        
        const materialsAvailable = await InventarioAllocationService.checkMaterialsAvailable(orden.id);
        
        if (!materialsAvailable) {
          componentLogger.warn('Materiales no disponibles - bloqueando apertura del modal', { 
            ordenId: orden.id, 
            numero: orden.numero 
          });
          
          onShowAlert(
            'warning', 
            'Materiales pendientes', 
            'No se puede iniciar la orden porque hay solicitudes de materiales pendientes o no hay suficiente material disponible. Debe aprobar todas las solicitudes en Inventarios > Solicitudes.'
          );
          return; // No abrir el modal
        }
        
        // Si los materiales están disponibles, abrir el modal
        setIsModalOpen(true);
      } catch (err) {
        componentLogger.error('Error verificando materiales antes de abrir modal', err);
        onShowAlert(
          'danger', 
          'Error de validación', 
          'No se pudo verificar la disponibilidad de materiales. Intente nuevamente.'
        );
      }
    }
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Función para activar la orden
  const handleActivateOrder = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser?.id) {
      componentLogger.error('Usuario no autenticado para activar orden', {
        ordenId: orden.id,
        numero: orden.numero,
        timestamp: new Date().toISOString()
      });
      onShowAlert('danger', 'Error de autenticación', 'Debe estar autenticado para activar órdenes.');
      return;
    }

    setIsActivating(true);
    const startTime = Date.now();
    
    try {
      componentLogger.info('Iniciando activación de orden', { 
        ordenId: orden.id, 
        numero: orden.numero, 
        usuario: currentUser.id,
        usuarioNombre: currentUser.name,
        timestamp: new Date().toISOString()
      });

      // La validación de materiales ya se hizo antes de abrir el modal
      componentLogger.info('Iniciando activación de orden (materiales ya validados)', { 
        ordenId: orden.id, 
        numero: orden.numero 
      });

      // Obtener repositorio de órdenes de producción
      const ordenRepository = RepositoryFactory.getRepository('orden_produccion');
      
      // Activar la orden usando el repositorio
      const result = await ordenRepository.activarOrden(orden.id, currentUser.id);

      componentLogger.info('Orden activada exitosamente', { 
        ordenId: orden.id, 
        logEstadoId: result.logEstadoId,
        usuario: currentUser.id,
        usuarioNombre: currentUser.name,
        timestamp: new Date().toISOString(),
        duracionOperacion: Date.now() - startTime
      });

      // Mostrar mensaje de éxito
      onShowAlert(
        'success', 
        'Orden activada exitosamente', 
        'La orden ha sido activada y comenzará el proceso de producción.'
      );

      // Cerrar modal y recargar lista
      setIsModalOpen(false);
      onReload();

    } catch (error) {
      componentLogger.error('Error al activar orden', { 
        ordenId: orden.id, 
        error: error.message 
      });

      // Determinar el tipo de error para mostrar mensaje apropiado
      let errorMessage = 'No se pudo activar la orden. Intente nuevamente.';
      let errorTitle = 'Error al activar orden';

      if (error.message.includes('ORDEN_YA_ACTIVA_ERROR')) {
        errorMessage = 'Esta orden ya está activa y no puede ser activada nuevamente.';
        errorTitle = 'Orden ya activa';
      } else if (error.message.includes('CREAR_LOG_ESTADO_ERROR')) {
        errorMessage = 'No se pudo crear el registro de estado. Intente nuevamente.';
      } else if (error.message.includes('ACTUALIZAR_ORDEN_ERROR')) {
        errorMessage = 'La orden se creó pero no se pudo actualizar. Contacte al administrador.';
      } else if (error.message.includes('log_estado')) {
        errorMessage = 'No se pudo crear el registro de estado. Intente nuevamente.';
      } else if (error.message.includes('orden_produccion')) {
        errorMessage = 'La orden se creó pero no se pudo actualizar. Contacte al administrador.';
      }

      onShowAlert('danger', errorTitle, errorMessage);
    } finally {
      setIsActivating(false);
    }
  };

  // Si la orden ya está activa, mostrar botón deshabilitado
  if (isOrdenActiva) {
    return (
      <Button 
        color="secondary" 
        size="sm" 
        className="me-1 px-2 py-1" 
        disabled
        title="Orden ya activa"
      >
        <i className="fas fa-check" data-testid="check-icon"></i>
      </Button>
    );
  }

  return (
    <>
      {/* Botón principal */}
      <Button 
        id={`iniciar-${orden.id}`}
        color="info" 
        size="sm" 
        className="me-1 px-2 py-1" 
        onClick={handleOpenModal}
        title="Iniciar Orden"
      >
        <i className="fas fa-play" data-testid="play-icon"></i>
      </Button>
      
      {/* Tooltip */}
      <UncontrolledTooltip placement="top" target={`iniciar-${orden.id}`}>
        Iniciar Orden
      </UncontrolledTooltip>

      {/* Modal de confirmación */}
      <Modal isOpen={isModalOpen} toggle={handleCloseModal} size="md" centered>
        <ModalHeader toggle={handleCloseModal}>
          <i className="fas fa-play text-info me-2"></i>
          Confirmar Activación de Orden
        </ModalHeader>
        
        <ModalBody>
          <div className="text-center">
            <i className="fas fa-question-circle text-warning" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3">¿Está seguro?</h5>
            <p className="text-muted">
              Está a punto de activar la orden <strong>#{orden.numero}</strong> - {orden.nombre_producto}.
            </p>
            <p className="text-muted">
              Una vez activada, la orden comenzará el proceso de producción y no podrá ser editada desde esta interfaz.
            </p>
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal} disabled={isActivating}>
            Cancelar
          </Button>
          <Button 
            color="info" 
            onClick={handleActivateOrder} 
            disabled={isActivating}
          >
            {isActivating ? (
              <>
                <Spinner size="sm" className="me-2" data-testid="spinner" />
                Activando...
              </>
            ) : (
              <>
                <i className="fas fa-play me-2"></i>
                Confirmar
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default IniciarOrdenButton;
